import { ActionTree } from 'vuex';
import ApiUrl from '../../../api/url';
import { IdpAccountState, NewIdpAccounts } from './types';
import { RootState } from '../../types';
import * as types from './mutation-types';
import { isHappenedOtherPartyError } from '../../error-type';
import { translator } from '../../translator';
import { AppContext } from '../../context';
const MessageKey = AppContext.MessageKey;

export const actions: ActionTree<IdpAccountState, RootState> = {
    /**
     * IdPアカウント一覧検索
     * @param {any} dispatch Vuex変数
     * @param {any} commit Vuex変数
     * @param {any} searchItems 検索の絞り込み項目
     * @param {any} pageNumber ページ番号
     * @param {any} perPage ページ毎の表示件数
     * @param {any} sort ソート条件
     * @returns {Promise<void>}
     */
    async search({ dispatch, commit }, { searchItems = {}, pageNumber, perPage, sort } = {}) {
        const payload = {
            word: searchItems.word,
            ignore_node_id: searchItems.ignore_node_id,
            idp_id: searchItems.idp_id,
            page_number: pageNumber,
            per_page: perPage,
            'sort[column]': sort.column,
            'sort[direction]': sort.direction,
        };
        try {
            commit(types.SEARCH_START);
            const resData = await dispatch(
                'auth/api',
                { method: 'get', url: ApiUrl.IdpAccount.SEARCH, params: payload },
                { root: true }
            );
            if (isHappenedOtherPartyError(resData)) {
                // システムエラー・予期せぬエラー時
                commit(types.FATAL_FAILURE);
            } else {
                // 成功時
                commit(types.SEARCH_SUCCESS, resData);
            }
        } catch (resData) {
            // アプリケーションエラー時
            commit(types.SEARCH_FAILURE, resData.errors[0]);
        }
    },

    /**
     * IdPアカウントのIdP内の検索
     * @param {any} dispatch Vuex変数
     * @param {any} commit Vuex変数
     * @param {any} id 検索対象のIdPのID
     * @param {any} name 検索するアカウント名
     * @returns {Promise<void>}
     */
    async directSearch({ dispatch, commit }, { id, name } = {}) {
        const payload = { id, name };
        try {
            commit(types.DIRECT_SEARCH_START);
            const resData = await dispatch(
                'auth/api',
                { method: 'post', url: ApiUrl.NodeIdp.ACCOUNT, params: payload },
                { root: true }
            );
            if (isHappenedOtherPartyError(resData)) {
                // システムエラー・予期せぬエラー時
                commit(types.FATAL_FAILURE);
            } else {
                // 成功時
                commit(types.DIRECT_SEARCH_SUCCESS, { idp_accounts: resData });
            }
        } catch (resData) {
            // アプリケーションエラー時
            commit(types.DIRECT_SEARCH_FAILURE, resData.errors[0]);
        }
    },

    /**
     * IdPアカウント新規登録
     * @param {any} dispatch Vuex変数
     * @param {any} commit Vuex変数
     * @param {any} rootState Vuex変数
     * @param {NewIdpAccounts} newIdpAccounts 新規登録IdPアカウント
     * @returns {Promise<void>}
     */
    async create({ dispatch, commit, rootState }, newIdpAccounts: NewIdpAccounts) {
        try {
            commit(types.CREATE_START);
            const resData = await dispatch(
                'auth/api',
                {
                    method: 'post',
                    url: ApiUrl.IdpAccount.CREATE,
                    params: newIdpAccounts,
                },
                { root: true }
            );
            if (isHappenedOtherPartyError(resData)) {
                // システムエラー・予期せぬエラー時
                commit(types.FATAL_FAILURE);
            } else {
                // 成功時
                commit(types.CREATE_SUCCESS);

                // 成功トースト表示
                const i18n = (rootState as any).i18n;
                const message = translator(i18n, MessageKey.IdpAccount.CREATED);
                dispatch('toastMessage', message, { root: true });
            }
        } catch (resData) {
            // アプリケーションエラー時
            const params = {
                errorTitle: resData.title,
                errorDetails: resData.errors,
            };
            commit(types.CREATE_FAILURE, params);
        }
    },

    /**
     * IdPアカウントの詳細情報取得
     * @param {any} dispatch Vuex変数
     * @param {any} commit Vuex変数
     * @param {number} id IdPアカウントID
     * @returns {Promise<void>}
     */
    async show({ dispatch, commit }, id: number) {
        try {
            commit(types.SHOW_START);
            const resData = await dispatch(
                'auth/api',
                {
                    method: 'get',
                    url: ApiUrl.IdpAccount.SHOW.replace(':id', id.toString()),
                },
                { root: true }
            );
            if (isHappenedOtherPartyError(resData)) {
                // システムエラー・予期せぬエラー時
                commit(types.FATAL_FAILURE);
            } else {
                // 成功時
                commit(types.SHOW_SUCCESS, resData);
            }
        } catch (resData) {
            // アプリケーションエラー時
            commit(types.SHOW_FAILURE, resData.errors[0]);
        }
    },

    /**
     * IdPアカウント削除
     * @param {any} dispatch Vuex変数
     * @param {any} commit Vuex変数
     * @param {any} rootState Vuex変数
     * @param {Array<number>} id IdPアカウントID
     * @returns {Promise<void>}
     */
    async destroy({ dispatch, commit, rootState }, id: Array<number>) {
        try {
            commit(types.DESTROY_START);
            const resData = await dispatch(
                'auth/api',
                {
                    method: 'delete',
                    url: ApiUrl.IdpAccount.DESTROY,
                    params: { id },
                },
                { root: true }
            );
            if (isHappenedOtherPartyError(resData)) {
                // システムエラー・予期せぬエラー時
                commit(types.FATAL_FAILURE);
            } else {
                // 成功時
                commit(types.DESTROY_SUCCESS);

                // 成功トースト表示
                const i18n = (rootState as any).i18n;
                const message = translator(i18n, MessageKey.IdpAccount.DESTROYED);
                dispatch('toastMessage', message, { root: true });
            }
        } catch (resData) {
            // アプリケーションエラー時
            const params = {
                errorTitle: resData.title,
                errorDetails: resData.errors,
            };
            commit(types.DESTROY_FAILURE, params);
        }
    },

    /**
     * 検索済みデータをクリアする
     * @param {any} commit Vuex変数
     * @returns {Promise<void>}
     */
    async clearWork({ commit }) {
        commit(types.CLEAR_WORK);
    },

    /**
     * エラーをクリアする
     * @param {any} commit Vuex変数
     * @returns {Promise<void>}
     */
    async resetError({ commit }) {
        commit(types.RESET_ERROR);
    },

    /**
     * 画面切り替え時のstoreの消去
     * @param {any} commit Vuex変数
     * @returns {Promise<void>}
     */
    async clearOnBeforeRoute({ commit }) {
        commit(types.CLEAR_ALL);
    },
};
